import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { getDatabase, ref, get, push, set, update, remove } from 'firebase/database';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  updateDoc,
  setDoc,
} from 'firebase/firestore';
import { getCachedValue, invalidateCachedValue, setCachedValue } from './cache';
import { getCanonicalMediaUrl, resolveMediaUrl } from './media';
import {
  normalizeProductPricing,
  prepareProductPricingForPersistence,
} from './productPricing';
import { normalizeOutOfStockFlag } from './productAvailability';
import { normalizeProductSizes, normalizeProductVariants } from './productInventory';
import { attachProductSlugs } from './productRoutes';

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

const hasFirebaseConfig = () =>
  Boolean(
    firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.databaseURL &&
    firebaseConfig.projectId
  );

const hasPublicFirestoreEnabled = () =>
  process.env.NEXT_PUBLIC_FIREBASE_ENABLE_FIRESTORE === 'true';

// ✅ SAFE initialization
let firebaseAppInstance = null;

try {
  if (!hasFirebaseConfig()) {
    console.warn('Firebase config missing. Features unavailable.');
  } else if (getApps().length > 0) {
    firebaseAppInstance = getApp();
  } else {
    firebaseAppInstance = initializeApp(firebaseConfig);
  }
} catch (error) {
  console.error('Firebase init failed:', error);
}

// Instance variables
let firebaseDbInstance = null;
let firebaseAuthInstance = null;
let firebaseFirestoreInstance = null;
let isClientFirestoreAvailable = hasPublicFirestoreEnabled();

// Constants
const PRODUCTS_CACHE_KEY = 'products_public_catalog';
const SITE_SETTINGS_CACHE_KEY = 'site_settings_public';
const PRODUCTS_CACHE_TTL_MS = 5 * 60 * 1000;
const SITE_SETTINGS_CACHE_TTL_MS = 5 * 60 * 1000;
const FIRESTORE_PRODUCTS_COLLECTION = 'products';
const FIRESTORE_SETTINGS_COLLECTION = 'settings';
const FIRESTORE_SITE_SETTINGS_DOCUMENT = 'site';
const LEGACY_PRODUCTS_PATH = 'shoes';
const DELETED_PRODUCTS_PATH = 'deletedProducts';
const LEGACY_SITE_SETTINGS_PATH = 'settings/site';

// Lazy getters
const getFirebaseDatabaseInstance = () => {
  if (firebaseDbInstance) return firebaseDbInstance;
  if (!firebaseAppInstance) return null;
  firebaseDbInstance = getDatabase(firebaseAppInstance);
  return firebaseDbInstance;
};

const getFirebaseFirestoreInstance = () => {
  if (!isClientFirestoreAvailable) return null;
  if (firebaseFirestoreInstance) return firebaseFirestoreInstance;
  if (!firebaseAppInstance) return null;
  firebaseFirestoreInstance = getFirestore(firebaseAppInstance);
  return firebaseFirestoreInstance;
};

const disableClientFirestore = () => {
  isClientFirestoreAvailable = false;
  firebaseFirestoreInstance = null;
};

const getFirebaseAuthInstance = () => {
  if (firebaseAuthInstance) return firebaseAuthInstance;
  if (!firebaseAppInstance) return null;
  firebaseAuthInstance = getAuth(firebaseAppInstance);
  return firebaseAuthInstance;
};

const requireFirebaseDatabase = () => {
  const db = getFirebaseDatabaseInstance();
  if (!db) throw new Error('Firebase database not configured.');
  return db;
};

const requireFirebaseFirestore = () => {
  const firestore = getFirebaseFirestoreInstance();
  if (!firestore) throw new Error('Firebase Firestore not configured.');
  return firestore;
};

const requireFirebaseAuth = () => {
  const auth = getFirebaseAuthInstance();
  if (!auth) throw new Error('Firebase auth not configured.');
  return auth;
};

export { requireFirebaseDatabase, requireFirebaseFirestore, requireFirebaseAuth };

// ==================== DEFAULTS ====================

export const DEFAULT_HOME_HERO_CONTENT = {
  eyebrow: 'Affordable footwear',
  title: 'Where Comfort Meets Style',
  description: 'Quality, trendy footwear at wholesale prices, even from one pair.',
  primaryCtaLabel: 'Shop now',
};

export const DEFAULT_SHOP_HERO_CONTENT = {
  heading: 'Browse every pair in one place.',
  description: 'Discover everyday shoes, occasion-ready pairs, kids picks, and clothing layers in one place.',
  primaryCtaLabel: 'Shop all shoes',
  secondaryCtaLabel: 'Explore the catalog',
};

export const DEFAULT_SHOP_COLLECTION_CARDS = [
  {
    key: 'Men',
    eyebrow: 'Featured collection',
    title: 'Everyday style picks',
    description: 'Casuals, sneakers, and officials selected for comfort, polish, and repeat wear.',
    ctaLabel: 'Browse collection',
  },
  {
    key: 'Women',
    eyebrow: 'Featured collection',
    title: 'Occasion-ready pairs',
    description: 'Heels, wedges, sandals, sneakers, brogues, and flats for every moment.',
    ctaLabel: 'Browse collection',
  },
  {
    key: 'Kids',
    eyebrow: 'Featured collection',
    title: 'Daily pairs for school and play',
    description: 'Easy, durable styles made for active days, school runs, and constant movement.',
    ctaLabel: 'Browse collection',
  },
  {
    key: 'Clothes',
    eyebrow: 'Featured collection',
    title: 'Tracksuits and sweater layers',
    description: 'Adult and kids tracksuits plus sweater layers in one clothing section.',
    ctaLabel: 'Browse collection',
  },
  {
    key: 'Bags',
    eyebrow: 'Featured collection',
    title: 'Carry options for work and weekends',
    description: 'Handbags, backpacks, totes, and travel-ready options for daily use.',
    ctaLabel: 'Browse collection',
  },
];

// ==================== HELPERS ====================

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value);
  return [];
};

const normalizeProduct = (product) => ({
  ...normalizeProductPricing(product),
  image: resolveMediaUrl(product?.image),
  logo: resolveMediaUrl(product?.logo),
  details: String(product?.details || '').trim(),
  reviews: String(product?.reviews || '').trim(),
  faqs: String(product?.faqs || '').trim(),
  isOutOfStock: normalizeOutOfStockFlag(product?.isOutOfStock),
  sizes: normalizeProductSizes(normalizeArray(product.sizes)),
  variants: normalizeProductVariants(normalizeArray(product.variants)).map((variant) => ({
    ...variant,
    image: resolveMediaUrl(variant?.image),
  })),
  description: String(product?.description || '').trim(),
});

const normalizeShopHeroContent = (content = {}) => ({
  heading: content.heading || DEFAULT_SHOP_HERO_CONTENT.heading,
  description: content.description || DEFAULT_SHOP_HERO_CONTENT.description,
  primaryCtaLabel: content.primaryCtaLabel || DEFAULT_SHOP_HERO_CONTENT.primaryCtaLabel,
  secondaryCtaLabel: content.secondaryCtaLabel || DEFAULT_SHOP_HERO_CONTENT.secondaryCtaLabel,
});

const normalizeShopCollectionCards = (cards = []) =>
  DEFAULT_SHOP_COLLECTION_CARDS.map((defaultCard) => {
    const matchingCard = normalizeArray(cards).find((card) => card?.key === defaultCard.key) || {};
    return {
      key: defaultCard.key,
      eyebrow: matchingCard.eyebrow || defaultCard.eyebrow,
      title: matchingCard.title || defaultCard.title,
      description: matchingCard.description || defaultCard.description,
      ctaLabel: matchingCard.ctaLabel || defaultCard.ctaLabel,
    };
  });

const normalizeSiteSettings = (settings = {}) => ({
  brandName: settings.brandName || 'Trendy Quest',
  logoUrl: resolveMediaUrl(settings.logoUrl || ''),
  heroProductIds: normalizeArray(settings.heroProductIds).slice(0, 3),
  homeHeroContent: {
    ...DEFAULT_HOME_HERO_CONTENT,
    ...((settings.homeHeroContent && typeof settings.homeHeroContent === 'object') ? settings.homeHeroContent : {}),
  },
  shopHeroContent: normalizeShopHeroContent(settings.shopHeroContent),
  shopCollectionCards: normalizeShopCollectionCards(settings.shopCollectionCards),
});

const mergeProductsById = (preferredProducts = [], fallbackProducts = []) => {
  const productMap = new Map();
  fallbackProducts.forEach((product) => {
    if (product?.id) productMap.set(product.id, product);
  });
  preferredProducts.forEach((product) => {
    if (product?.id) productMap.set(product.id, product);
  });
  return Array.from(productMap.values());
};

const mergeSiteSettings = (preferredSettings = {}, fallbackSettings = {}) =>
  normalizeSiteSettings({
    ...fallbackSettings,
    ...preferredSettings,
    homeHeroContent: {
      ...(fallbackSettings.homeHeroContent || {}),
      ...(preferredSettings.homeHeroContent || {}),
    },
    shopHeroContent: {
      ...(fallbackSettings.shopHeroContent || {}),
      ...(preferredSettings.shopHeroContent || {}),
    },
    heroProductIds:
      preferredSettings.heroProductIds !== undefined
        ? preferredSettings.heroProductIds
        : fallbackSettings.heroProductIds,
    shopCollectionCards:
      preferredSettings.shopCollectionCards !== undefined
        ? preferredSettings.shopCollectionCards
        : fallbackSettings.shopCollectionCards,
  });

const prepareProductForPersistence = (product = {}) => {
  const preparedProduct = prepareProductPricingForPersistence(product);

  return {
    ...preparedProduct,
    image: getCanonicalMediaUrl(product?.image),
    logo: getCanonicalMediaUrl(product?.logo),
    details: String(product?.details || '').trim(),
    reviews: String(product?.reviews || '').trim(),
    faqs: String(product?.faqs || '').trim(),
    isOutOfStock: normalizeOutOfStockFlag(product?.isOutOfStock),
    variants: normalizeArray(product?.variants).map((variant) => ({
      ...variant,
      image: getCanonicalMediaUrl(variant?.image),
    })),
  };
};

const prepareProductUpdatesForPersistence = (updates = {}) => {
  const preparedUpdates = prepareProductPricingForPersistence(updates);
  const hasUpdate = (key) => Object.prototype.hasOwnProperty.call(updates, key);

  return {
    ...preparedUpdates,
    ...(hasUpdate('image') ? { image: getCanonicalMediaUrl(updates.image) } : {}),
    ...(hasUpdate('logo') ? { logo: getCanonicalMediaUrl(updates.logo) } : {}),
    ...(hasUpdate('details') ? { details: String(updates.details || '').trim() } : {}),
    ...(hasUpdate('reviews') ? { reviews: String(updates.reviews || '').trim() } : {}),
    ...(hasUpdate('faqs') ? { faqs: String(updates.faqs || '').trim() } : {}),
    ...(hasUpdate('isOutOfStock')
      ? { isOutOfStock: normalizeOutOfStockFlag(updates.isOutOfStock) }
      : {}),
    ...(hasUpdate('variants')
      ? {
          variants: normalizeArray(updates.variants).map((variant) => ({
            ...variant,
            image: getCanonicalMediaUrl(variant?.image),
          })),
        }
      : {}),
  };
};

const prepareSiteSettingsForPersistence = (settings = {}) => ({
  ...settings,
  logoUrl: getCanonicalMediaUrl(settings?.logoUrl),
});

const isFirestoreOfflineError = (error) =>
  error && (
    error.code === 'unavailable' ||
    error.code === 'network-request-failed' ||
    error.code === 'deadline-exceeded' ||
    error.message?.toLowerCase().includes('client is offline') ||
    error.message?.toLowerCase().includes('offline') ||
    error.message?.toLowerCase().includes('failed to get document')
  );

const isFirestoreMissingDatabaseError = (error) => {
  const code = String(error?.code || '').toLowerCase();
  const message = String(error?.message || '').toLowerCase();

  return (
    code === 'not-found' ||
    (message.includes('database') && message.includes('not found')) ||
    (message.includes('firestore') && message.includes('not found'))
  );
};

const mapFirestoreProductDocs = (snapshot) =>
  snapshot.docs.map((productDoc) => normalizeProduct({ id: productDoc.id, ...productDoc.data() }));

// ==================== FETCH FUNCTIONS ====================

const fetchProductsFromLegacyDatabase = async () => {
  const db = getFirebaseDatabaseInstance();
  if (!db) return [];
  const snap = await get(ref(db, LEGACY_PRODUCTS_PATH));
  if (!snap.exists()) return [];
  const data = snap.val();
  return Object.keys(data).map((key) => normalizeProduct({ id: key, ...data[key] }));
};

export const fetchDeletedProductIds = async () => {
  const db = getFirebaseDatabaseInstance();
  let deletedProducts;

  if (db) {
    const snapshot = await get(ref(db, DELETED_PRODUCTS_PATH));
    deletedProducts = snapshot.exists() ? snapshot.val() : {};
  } else if (firebaseConfig.databaseURL) {
    const response = await fetch(`${firebaseConfig.databaseURL}/${DELETED_PRODUCTS_PATH}.json`, {
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error(`Could not read deleted product records (${response.status}).`);
    }
    deletedProducts = await response.json();
  } else {
    return [];
  }

  return Object.entries(deletedProducts || {})
    .filter(([, value]) => Boolean(value))
    .map(([id]) => id);
};

export const fetchProducts = async ({ forceFresh = false } = {}) => {
  if (!forceFresh) {
    const cachedProducts = getCachedValue(PRODUCTS_CACHE_KEY);
    if (cachedProducts) return cachedProducts;
  }

  const firestore = getFirebaseFirestoreInstance();
  let firestoreProducts = [];

  if (firestore) {
    try {
      const snapshot = await getDocs(collection(firestore, FIRESTORE_PRODUCTS_COLLECTION));
      firestoreProducts = mapFirestoreProductDocs(snapshot);
    } catch (error) {
      if (isFirestoreMissingDatabaseError(error)) {
        disableClientFirestore();
      } else if (!isFirestoreOfflineError(error)) {
        console.warn('Could not read products from Firestore:', error);
      }
    }
  }

  let deletedProductIdSet = new Set();
  try {
    deletedProductIdSet = new Set(await fetchDeletedProductIds());
  } catch (error) {
    console.warn('Could not filter deleted products from catalog:', error);
  }

  try {
    const legacyProducts = await fetchProductsFromLegacyDatabase();
    return setCachedValue(
      PRODUCTS_CACHE_KEY,
      attachProductSlugs(
        mergeProductsById(firestoreProducts, legacyProducts).filter(
          (product) => !deletedProductIdSet.has(product.id)
        )
      ),
      PRODUCTS_CACHE_TTL_MS
    );
  } catch (error) {
    console.warn('Could not read products from RTD fallback:', error);
  }

  return setCachedValue(
    PRODUCTS_CACHE_KEY,
    attachProductSlugs(
      firestoreProducts.filter((product) => !deletedProductIdSet.has(product.id))
    ),
    PRODUCTS_CACHE_TTL_MS
  );
};

export const fetchAdminProducts = async ({ forceFresh = false } = {}) => {
  if (!forceFresh) {
    const cachedProducts = getCachedValue(PRODUCTS_CACHE_KEY);
    if (cachedProducts) return cachedProducts;
  }

  const currentUser = getFirebaseAuthInstance()?.currentUser;
  if (!currentUser) {
    throw new Error('Sign in as an admin before loading products.');
  }

  const idToken = await currentUser.getIdToken();
  const response = await fetch('/api/products', {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });
  const result = await response.json().catch(() => null);

  if (!response.ok || result?.ok !== true || !Array.isArray(result.products)) {
    throw new Error(result?.error || `Could not load admin products (${response.status}).`);
  }

  return setCachedValue(PRODUCTS_CACHE_KEY, result.products, PRODUCTS_CACHE_TTL_MS);
};

export const fetchOrders = async () => {
  const db = requireFirebaseDatabase();
  const snap = await get(ref(db, 'orders'));
  if (snap.exists()) {
    const data = snap.val();
    return Object.keys(data).map((key) => ({ id: key, ...data[key] }));
  }
  return [];
};

export const fetchOrderById = async (id) => {
  if (!id) return null;
  const db = requireFirebaseDatabase();
  const snap = await get(ref(db, `orders/${id}`));
  if (!snap.exists()) return null;
  return { id, ...snap.val() };
};

export const deleteOrder = async (id) => {
  if (!id) throw new Error('Order id required');
  const db = requireFirebaseDatabase();
  await remove(ref(db, `orders/${id}`));
};

export const findOrderByMpesaCheckoutRequestId = async (checkoutRequestId) => {
  if (!checkoutRequestId) return null;
  const orders = await fetchOrders();
  return orders.find((order) => order?.payment?.checkoutRequestId === checkoutRequestId) || null;
};

export const fetchSiteSettings = async ({ forceFresh = false } = {}) => {
  if (!forceFresh) {
    const cachedSettings = getCachedValue(SITE_SETTINGS_CACHE_KEY);
    if (cachedSettings) return cachedSettings;
  }

  const firestore = getFirebaseFirestoreInstance();
  let firestoreSettings = null;
  let legacySettings = null;

  if (firestore) {
    try {
      const siteSettingsSnapshot = await getDoc(
        doc(firestore, FIRESTORE_SETTINGS_COLLECTION, FIRESTORE_SITE_SETTINGS_DOCUMENT)
      );
      if (siteSettingsSnapshot.exists()) {
        firestoreSettings = siteSettingsSnapshot.data();
      }
    } catch (error) {
      if (isFirestoreMissingDatabaseError(error)) {
        disableClientFirestore();
      } else if (!isFirestoreOfflineError(error)) {
        console.warn('Could not read site settings from Firestore:', error);
      }
    }
  }

  try {
    const db = getFirebaseDatabaseInstance();
    if (db) {
      const snap = await get(ref(db, LEGACY_SITE_SETTINGS_PATH));
      if (snap.exists()) {
        legacySettings = snap.val();
      }
    }
  } catch (error) {
    console.warn('Could not read site settings from RTD fallback:', error);
  }

  return setCachedValue(
    SITE_SETTINGS_CACHE_KEY,
    mergeSiteSettings(firestoreSettings || {}, legacySettings || {}),
    SITE_SETTINGS_CACHE_TTL_MS
  );
};

// ==================== AUTH FUNCTIONS ====================

export const signInAdminUser = async (email, password) => {
  const auth = requireFirebaseAuth();
  await setPersistence(auth, browserLocalPersistence);
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return credential.user;
};

export const registerCustomerUser = async ({ email, password, firstName, lastName }) => {
  const auth = requireFirebaseAuth();
  const db = requireFirebaseDatabase();
  await setPersistence(auth, browserLocalPersistence);
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  const user = credential.user;

  await set(ref(db, `customers/${user.uid}/profile`), {
    email: user.email || email,
    firstName: firstName || '',
    lastName: lastName || '',
    createdAt: Date.now(),
  });

  return user;
};

export const signInCustomerUser = async (email, password) => {
  const auth = requireFirebaseAuth();
  await setPersistence(auth, browserLocalPersistence);
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return credential.user;
};

export const signInWithGoogleCustomerUser = async () => {
  const auth = requireFirebaseAuth();
  const db = requireFirebaseDatabase();
  const provider = new GoogleAuthProvider();
  await setPersistence(auth, browserLocalPersistence);
  const credential = await signInWithPopup(auth, provider);
  const user = credential.user;

  const existingProfileSnap = await get(ref(db, `customers/${user.uid}/profile`));
  if (!existingProfileSnap.exists()) {
    const displayNameParts = (user.displayName || '').trim().split(/\s+/).filter(Boolean);
    const firstName = displayNameParts[0] || '';
    const lastName = displayNameParts.slice(1).join(' ');

    await set(ref(db, `customers/${user.uid}/profile`), {
      email: user.email || '',
      firstName,
      lastName,
      createdAt: Date.now(),
      provider: 'google',
    });
  }

  return user;
};

export const signOutCustomerUser = async () => {
  const auth = requireFirebaseAuth();
  await signOut(auth);
};

export const getCurrentCustomerIdToken = async () => {
  const auth = getFirebaseAuthInstance();
  const currentUser = auth?.currentUser;
  if (!currentUser) return '';
  return currentUser.getIdToken();
};

export const watchCustomerSession = (callback) => {
  const auth = getFirebaseAuthInstance();
  const db = getFirebaseDatabaseInstance();

  if (!auth || !db) {
    callback({ user: null, profile: null, authReady: true, accessError: new Error('Firebase auth is not configured.') });
    return () => {};
  }

  return onAuthStateChanged(auth, async (user) => {
    if (!user) {
      callback({ user: null, profile: null, authReady: true, accessError: null });
      return;
    }

    try {
      const profileSnap = await get(ref(db, `customers/${user.uid}/profile`));
      callback({
        user,
        profile: profileSnap.exists() ? profileSnap.val() : null,
        authReady: true,
        accessError: null,
      });
    } catch (error) {
      console.warn('Could not read customer profile:', error);
      callback({ user, profile: null, authReady: true, accessError: error });
    }
  });
};

export const fetchCustomerCart = async (uid) => {
  if (!uid) return [];
  const db = requireFirebaseDatabase();
  const snap = await get(ref(db, `customers/${uid}/cart`));
  return normalizeArray(snap.val()).map((item) => normalizeProductPricing(item));
};

export const saveCustomerCart = async (uid, cartItems) => {
  if (!uid) return;
  const db = requireFirebaseDatabase();
  await set(ref(db, `customers/${uid}/cart`), normalizeArray(cartItems));
};

export const fetchCustomerOrders = async (uid) => {
  if (!uid) return [];
  const db = requireFirebaseDatabase();
  const snap = await get(ref(db, `customers/${uid}/orders`));
  if (!snap.exists()) return [];

  const data = snap.val();
  return Object.keys(data)
    .map((key) => ({ id: key, ...data[key] }))
    .sort((left, right) => (right.createdAt || right.updatedAt || 0) - (left.createdAt || left.updatedAt || 0));
};

export const signOutAdminUser = async () => {
  const auth = requireFirebaseAuth();
  await signOut(auth);
};

export const watchAdminSession = (callback) => {
  const auth = getFirebaseAuthInstance();
  const db = getFirebaseDatabaseInstance();

  if (!auth || !db) {
    callback({ user: null, isAdmin: false, accessError: new Error('Firebase auth is not configured.') });
    return () => {};
  }

  return onAuthStateChanged(auth, async (user) => {
    if (!user) {
      callback({ user: null, isAdmin: false, accessError: null });
      return;
    }

    try {
      const adminSnap = await get(ref(db, `admins/${user.uid}`));
      callback({ user, isAdmin: adminSnap.exists(), accessError: null });
    } catch (error) {
      console.warn('Could not verify admin access:', error);
      callback({ user, isAdmin: false, accessError: error });
    }
  });
};

// ==================== PRODUCT CRUD (RTD-ONLY WORKAROUND) ====================

// Temporary workaround - forces RTD-only to avoid Firestore init conflicts
const USE_RTD_ONLY = true; // Set to false once Firestore is fixed

const triggerStorefrontRevalidation = async (productId, product = null) => {
  if (typeof window === 'undefined') return;

  const auth = getFirebaseAuthInstance();
  const currentUser = auth?.currentUser;

  if (!currentUser) {
    console.warn('Skipping storefront revalidation because there is no authenticated admin session.');
    return;
  }

  try {
    const idToken = await currentUser.getIdToken();
    const response = await fetch('/api/revalidate-storefront', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(
        productId
          ? {
              productId,
              ...(product
                ? {
                    product: {
                      id: productId,
                      name: product.name || '',
                      slug: product.slug || '',
                      department: product.department || '',
                      subcategory: product.subcategory || '',
                      category: product.category || '',
                      subdivision: product.subdivision || '',
                      variants: Array.isArray(product.variants)
                        ? product.variants.map((variant) => ({
                            name: variant?.name || '',
                          }))
                        : [],
                    },
                  }
                : {}),
            }
          : {}
      ),
    });

    let payload = null;

    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok || payload?.ok === false) {
      console.warn('Storefront revalidation reported a failure:', payload || response.statusText);
    }
  } catch (error) {
    console.warn('Storefront revalidation request failed:', error);
  }
};

export const addProduct = async (product) => {
  const preparedProduct = prepareProductForPersistence(product);

  if (USE_RTD_ONLY) {
    const db = getFirebaseDatabaseInstance();
    if (!db) throw new Error('RTD not available');

    const productRef = push(ref(db, LEGACY_PRODUCTS_PATH));
    const productId = productRef.key;
    await set(productRef, {
      ...preparedProduct,
      id: productId,
      createdAt: Date.now(),
      updatedAt: Date.now()
    });

    invalidateCachedValue(PRODUCTS_CACHE_KEY);
    await triggerStorefrontRevalidation(productId);
    console.log('✅ Product added to RTD (RTD-only mode):', productId);
    return productId;
  }

  // Firestore + RTD logic (disabled for now)
  const firestore = getFirebaseFirestoreInstance();
  const db = getFirebaseDatabaseInstance();
  let productId = null;
  let firestoreError = null;

  const productPayload = {
    ...preparedProduct,
    createdAt: preparedProduct.createdAt || Date.now(),
    updatedAt: Date.now()
  };

  if (firestore) {
    try {
      const productDocument = await addDoc(
        collection(firestore, FIRESTORE_PRODUCTS_COLLECTION),
        productPayload
      );
      productId = productDocument.id;
      console.log('✅ Product added to Firestore:', productId);
    } catch (error) {
      firestoreError = error;
      if (isFirestoreMissingDatabaseError(error)) {
        disableClientFirestore();
      }
      console.warn('⚠️ Firestore add failed:', error.code, error.message);
    }
  }

  if (productId && db) {
    try {
      await set(ref(db, `${LEGACY_PRODUCTS_PATH}/${productId}`), { ...productPayload, id: productId });
    } catch (mirrorError) {
      console.warn('⚠️ RTD mirror failed:', mirrorError.message);
    }
  }

  if (!productId) {
    if (!db) {
      throw firestoreError || new Error('No writable Firebase backend available.');
    }

    const productRef = push(ref(db, LEGACY_PRODUCTS_PATH));
    productId = productRef.key;
    await set(productRef, { ...productPayload, id: productId });
    console.log('✅ Product added to RTD:', productId);
  }

  invalidateCachedValue(PRODUCTS_CACHE_KEY);
  await triggerStorefrontRevalidation(productId);
  return productId;
};

export const updateProduct = async (id, updates) => {
  if (!id) throw new Error('Product ID is required for update');
  const preparedUpdates = prepareProductUpdatesForPersistence(updates);

  if (USE_RTD_ONLY) {
    const db = getFirebaseDatabaseInstance();
    if (!db) throw new Error('RTD not available');

    const updatesWithMeta = {
      ...preparedUpdates,
      id,
      updatedAt: Date.now()
    };

    await update(ref(db, `${LEGACY_PRODUCTS_PATH}/${id}`), updatesWithMeta);
    invalidateCachedValue(PRODUCTS_CACHE_KEY);
    await triggerStorefrontRevalidation(id);
    console.log('✅ Product updated in RTD (RTD-only mode):', id);
    return;
  }

  const firestore = getFirebaseFirestoreInstance();
  const db = getFirebaseDatabaseInstance();
  let firestoreUpdated = false;
  let firestoreError = null;

  const updatesWithMeta = {
    ...preparedUpdates,
    id,
    updatedAt: Date.now()
  };

  if (firestore) {
    try {
      const productRef = doc(firestore, FIRESTORE_PRODUCTS_COLLECTION, id);
      const docSnap = await getDoc(productRef);

      if (docSnap.exists()) {
        await updateDoc(productRef, updatesWithMeta);
      } else {
        await setDoc(productRef, {
          ...updatesWithMeta,
          createdAt: updatesWithMeta.createdAt || Date.now()
        });
      }

      firestoreUpdated = true;
      console.log('✅ Product updated in Firestore:', id);
    } catch (error) {
      firestoreError = error;
      if (isFirestoreMissingDatabaseError(error)) {
        disableClientFirestore();
      }
      console.warn('⚠️ Firestore update failed:', error.code, error.message);
    }
  }

  if (db) {
    try {
      await update(ref(db, `${LEGACY_PRODUCTS_PATH}/${id}`), updatesWithMeta);
      console.log('✅ Product updated in RTD:', id);
    } catch (rtdError) {
      console.warn('⚠️ RTD update failed:', rtdError.message);
      if (!firestoreUpdated) {
        throw firestoreError || rtdError;
      }
    }
  } else if (!firestoreUpdated) {
    throw firestoreError || new Error('No writable Firebase backend available.');
  }

  invalidateCachedValue(PRODUCTS_CACHE_KEY);
  await triggerStorefrontRevalidation(id);
};

export const deleteProduct = async (id, product = null) => {
  if (!id) throw new Error('Product ID is required for deletion');

  const auth = getFirebaseAuthInstance();
  const currentUser = auth?.currentUser;
  if (!currentUser) {
    throw new Error('Sign in as an admin before deleting a product.');
  }

  const idToken = await currentUser.getIdToken();
  const response = await fetch(`/api/products/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  const result = await response.json().catch(() => null);
  if (!response.ok || result?.ok !== true) {
    throw new Error(result?.error || `Product deletion failed (${response.status}).`);
  }

  invalidateCachedValue(PRODUCTS_CACHE_KEY);
  await triggerStorefrontRevalidation(id, product);
  return { warning: result.warning || '' };
};

// ==================== ORDERS & SETTINGS ====================

export const createOrder = async (order) => {
  const db = requireFirebaseDatabase();
  const record = push(ref(db, 'orders'));
  await set(record, { ...order, timestamp: Date.now() });
  return record.key;
};

export const updateOrderStatus = async (id, status) => {
  const db = requireFirebaseDatabase();
  await update(ref(db, `orders/${id}`), { status });
};

export const updateOrder = async (id, updates) => {
  const db = requireFirebaseDatabase();
  await update(ref(db, `orders/${id}`), updates);
};

export const updateSiteSettings = async (settings) => {
  const normalizedSettings = normalizeSiteSettings(prepareSiteSettingsForPersistence(settings));
  const firestore = getFirebaseFirestoreInstance();
  let firestoreUpdated = false;

  if (firestore) {
    try {
      await setDoc(
        doc(firestore, FIRESTORE_SETTINGS_COLLECTION, FIRESTORE_SITE_SETTINGS_DOCUMENT),
        normalizedSettings
      );
      firestoreUpdated = true;
    } catch (error) {
      if (isFirestoreMissingDatabaseError(error)) {
        disableClientFirestore();
      } else if (!isFirestoreOfflineError(error)) {
        throw error;
      }
    }
  }

  const db = getFirebaseDatabaseInstance();
  if (db) {
    await set(ref(db, LEGACY_SITE_SETTINGS_PATH), normalizedSettings);
  } else if (!firestoreUpdated) {
    throw new Error('Unable to save site settings: no writable Firebase backend available.');
  }

  invalidateCachedValue(SITE_SETTINGS_CACHE_KEY);
};
